import React, { FC, useState } from "react";
import { useElementId } from "../../hooks";
import { FormField, FormFieldProps } from "../form-field";
import { InputProps } from "../input/input.types";
import { DateRange, DateRangeProps } from "./date-range";
import { DateTime, DateTimeProps } from "./date-time";

export interface DateTimeInputOwnProps {
  /**
   * This props date picker is whether range picker or not
   */
  selectsRange?: boolean;

  /**
   * This prop will be called on date is selected.
   */
  onDateSelect: (startDate: Date | null, endDate?: Date | null) => void;

  /**
   * Specifies the formField class
   */
  formFieldClassName?: string;

  /**
   * Color option for the certain Input component elements - focus border, icon enhancer etc.
   *
   * @default "interactive"
   */
  color?: InputProps["color"];
}

export type DateTimeInputToDateTimePassThroughProps = Omit<
  DateTimeProps,
  keyof DateTimeInputOwnProps | "selectsStart" | "selectsEnd" | "timeIntervals" | "withTime"
>;

export type DateTimeInputToDateRangePassThroughProps = Omit<
  DateRangeProps,
  keyof DateTimeInputOwnProps | keyof DateTimeInputToDateTimePassThroughProps
>;

export type DateTimeInputToFormFieldPassThroughProps = Omit<
  FormFieldProps,
  keyof DateTimeInputOwnProps | "formFieldId" | "formFieldType" | "children" | "focused" | "textLength" | "readOnly"
>;

export type DateTimeInputProps = DateTimeInputOwnProps &
  DateTimeInputToDateTimePassThroughProps &
  DateTimeInputToFormFieldPassThroughProps &
  DateTimeInputToDateRangePassThroughProps;

/**
 * The `DateTimeInput` component represents a form field meant to capture date, time or a combination of date-time user input.
 */
export const DateTimeInput: FC<DateTimeInputProps> = ({
  /* DateTimeInput props */

  id,
  className,
  selectsRange = false,
  formFieldClassName,
  color = "interactive",
  /* FormField pass through props */
  formFieldMessageId,
  label,
  labelInfo,
  hideLabel,
  labelProps,
  secondaryAction,
  hint,
  success,
  error,
  required,
  disabled,
  onBlur,
  onFocus,

  ...rest
}) => {
  const formFieldId = useElementId(id, "date-time-input");
  const [focused, setFocused] = useState(false);

  return (
    <FormField
      formFieldId={formFieldId}
      formFieldMessageId={formFieldMessageId}
      formFieldType="other"
      focused={focused}
      label={label}
      labelInfo={labelInfo}
      hideLabel={hideLabel}
      labelProps={labelProps}
      secondaryAction={secondaryAction}
      hint={hint}
      success={success}
      error={error}
      required={required}
      disabled={disabled}
      className={formFieldClassName}
    >
      {() => (
        <>
          {!selectsRange && (
            <DateTime
              disabled={disabled}
              {...rest}
              onFocus={(e) => {
                setFocused(true);
                onFocus && onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur && onBlur(e);
              }}
              className={className}
              id={formFieldId}
              color={color}
            />
          )}
          {selectsRange && (
            <DateRange
              disabled={disabled}
              {...rest}
              onFocus={(e) => {
                setFocused(true);
                onFocus && onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur && onBlur(e);
              }}
              className={className}
              id={formFieldId}
              color={color}
            />
          )}
        </>
      )}
    </FormField>
  );
};
